/**
 * Reason codes attached to attribution decisions and commissions
 * (design spec §6.3 plus lifecycle reasons), with Persian explanations.
 *
 * Rule: texts never name another affiliate.
 */

export const REASON_CODES = [
  'DOMAIN_ONLY',
  'REFUNDED_IN_GUARANTEE',
  'SELF_REFERRAL',
  'ATTRIBUTED_TO_OTHER_CODE',
  'BOUND_TO_OTHER_AFFILIATE',
  'NO_SIGNAL',
  'CODE_LIMIT_REACHED',
  'SERVICE_NOT_ACTIVE',
  'UNDER_REVIEW',
  'NOT_ELIGIBLE_PRODUCT',
  'REFUNDED_AFTER_APPROVAL',
  'FRAUD',
  'MANUAL_ASSIGNMENT',
  'PRE_CUTOVER',
  'CANCELLED_EARLY',
  'AFFILIATE_CLOSED',
  'CAMPAIGN_DISCOUNT',
  'AFFILIATE_SUSPENDED',
] as const;

export type ReasonCode = (typeof REASON_CODES)[number];

export function isReasonCode(value: unknown): value is ReasonCode {
  return typeof value === 'string' && (REASON_CODES as readonly string[]).includes(value);
}

interface ReasonCopy {
  /** Why this happened. */
  text: string;
  /** What the affiliate can do next. */
  nextStep: string;
}

export const REASONS_FA: Readonly<Record<ReasonCode, ReasonCopy>> = Object.freeze({
  DOMAIN_ONLY: {
    text: 'این سفارش فقط ثبت یا تمدید دامنه بود و دامنه پورسانت ندارد.',
    nextStep: 'هاست، سرور و سرویس‌های دیگر پورسانت دارند؛ در سفارش‌های بعدی مشتری را به این سرویس‌ها راهنمایی کنید.',
  },
  REFUNDED_IN_GUARANTEE: {
    text: 'مشتری در دورهٔ ضمانت انصراف داد و مبلغ را پس گرفت، برای همین پورسانت این سفارش لغو شد.',
    nextStep: 'کار خاصی لازم نیست. اگر فکر می‌کنید بازپرداخت اشتباه ثبت شده، از پشتیبانی پیگیری کنید.',
  },
  SELF_REFERRAL: {
    text: 'این خرید با حساب یا مشخصات خود شما انجام شده است و خرید برای خودتان پورسانت ندارد.',
    nextStep: 'پورسانت برای فروش به مشتریان دیگر ساخته می‌شود. اگر خریدار شخص دیگری است، ادعا ثبت کنید تا بررسی کنیم.',
  },
  ATTRIBUTED_TO_OTHER_CODE: {
    text: 'مشتری هنگام پرداخت کد بازاریاب دیگری را وارد کرد و سفارش به همان کد تعلق گرفت.',
    nextStep: 'به مشتریانتان یادآوری کنید هنگام پرداخت کد شما را وارد کنند. اگر فکر می‌کنید اشتباهی پیش آمده، ادعا ثبت کنید.',
  },
  BOUND_TO_OTHER_AFFILIATE: {
    text: 'این مشتری پیش‌تر از طریق بازاریاب دیگری آمده بود و هنوز در دورهٔ اتصال به آن بازاریاب است.',
    nextStep: 'اگر مشتری در خرید بعدی کد تخفیف شما را وارد کند، آن سفارش به شما تعلق می‌گیرد. اگر فکر می‌کنید این سفارش مال شماست، ادعا ثبت کنید.',
  },
  NO_SIGNAL: {
    text: 'در این سفارش نشانه‌ای از لینک، کد معرف یا کد تخفیف شما پیدا نشد.',
    nextStep: 'اگر فکر می‌کنید این سفارش مال شماست، تا ۳۰ روز پس از سفارش ادعا ثبت کنید تا بررسی کنیم.',
  },
  CODE_LIMIT_REACHED: {
    text: 'سقف استفاده از این کد پر شده بود و کد روی این سفارش اعمال نشد.',
    nextStep: 'در بخش «کدها» یک کد تازه بسازید یا کدهای قدیمی را مرتب کنید تا جا برای کد جدید باز شود.',
  },
  SERVICE_NOT_ACTIVE: {
    text: 'سرویس این سفارش در پایان دورهٔ ضمانت فعال نبود (تعلیق، لغو یا حذف شده بود).',
    nextStep: 'اگر سرویس دوباره فعال شده است، به پشتیبانی خبر بدهید تا وضعیت پورسانت دوباره بررسی شود.',
  },
  UNDER_REVIEW: {
    text: 'این سفارش برای بررسی بیشتر نگه داشته شده است. این بررسی معمولاً چند روز طول می‌کشد.',
    nextStep: 'کاری لازم نیست. نتیجه را در همین صفحه و با پیامک به شما اطلاع می‌دهیم.',
  },
  NOT_ELIGIBLE_PRODUCT: {
    text: 'این محصول در فهرست محصولات دارای پورسانت نیست.',
    nextStep: 'فهرست محصولات دارای پورسانت را در صفحهٔ «قوانین» ببینید و مشتری را به همان محصولات راهنمایی کنید.',
  },
  REFUNDED_AFTER_APPROVAL: {
    text: 'مشتری پس از تأیید پورسانت بخشی یا همهٔ مبلغ را پس گرفت و به همان نسبت از پورسانت کم شد.',
    nextStep: 'لازم نیست مبلغی پرداخت کنید؛ این کسری از پورسانت‌های بعدی شما برداشته می‌شود.',
  },
  FRAUD: {
    text: 'این سفارش در بررسی امنیتی نامعتبر تشخیص داده شد و پورسانت ندارد.',
    nextStep: 'اگر توضیح یا مدرکی دارید، از طریق پشتیبانی برای ما بفرستید تا دوباره بررسی کنیم.',
  },
  MANUAL_ASSIGNMENT: {
    text: 'تیم میزبان‌فا پس از بررسی، این سفارش را به نام شما ثبت کرد.',
    nextStep: 'کاری لازم نیست. پورسانت این سفارش هم مثل بقیه، دورهٔ ضمانت را طی می‌کند و بعد قابل برداشت می‌شود.',
  },
  PRE_CUTOVER: {
    text: 'این فاکتور پیش از انتقال به سیستم جدید پرداخت شده و پورسانتش در سیستم بازاریابی قبلی میزبان‌فا حساب شده است.',
    nextStep: 'کاری لازم نیست. پورسانت این فروش در موجودی منتقل‌شده از سیستم قبلی آمده است.',
  },
  CANCELLED_EARLY: {
    text: 'مشتری مدت کوتاهی پس از تأیید پورسانت، سرویس را لغو کرد و پورسانت آن پس گرفته شد.',
    nextStep: 'لازم نیست مبلغی پرداخت کنید؛ این کسری از پورسانت‌های بعدی شما برداشته می‌شود.',
  },
  CAMPAIGN_DISCOUNT: {
    text: 'کمپین فروش به هدف نرسید و تخفیفی که با این کد بیشتر از سقف سطح خودتان داده بودید، کل پورسانت این فروش را پوشاند.',
    nextStep: 'کاری لازم نیست. فروش‌های بعدی با سقف تخفیف سطح خودتان حساب می‌شوند.',
  },
  AFFILIATE_CLOSED: {
    text: 'حساب بازاریابی شما بسته شده است و برای خریدها و تمدیدهای بعد از بسته شدن حساب، پورسانتی حساب نمی‌شود.',
    nextStep: 'اگر فکر می‌کنید حساب شما اشتباهی بسته شده است، از طریق پشتیبانی با ما تماس بگیرید.',
  },
  AFFILIATE_SUSPENDED: {
    text: 'حساب بازاریابی شما فعلاً معلق است. پورسانت این سفارش ثبت شده، ولی تا روشن شدن وضعیت حساب نگه داشته می‌شود.',
    nextStep: 'کاری لازم نیست. اگر حساب شما دوباره فعال شود، این پورسانت هم آزاد می‌شود و مثل بقیه، دورهٔ ضمانت را طی می‌کند.',
  },
});

/** Persian explanation of a reason code. Unknown codes get a generic text. */
export function reasonText(code: string): string {
  return isReasonCode(code) ? REASONS_FA[code].text : 'وضعیت این سفارش در حال بررسی است.';
}

/** Persian "what you can do next" text for a reason code. Unknown codes point to support. */
export function reasonNextStep(code: string): string {
  return isReasonCode(code)
    ? REASONS_FA[code].nextStep
    : 'اگر سؤالی دربارهٔ این سفارش دارید، از طریق پشتیبانی با ما در تماس باشید.';
}
